/**
 * @fileoverview Home Selections Component (Admin Portal)
 *
 * Displays client home selections organized into tabs:
 * - Overview Tab: 5 sections (Exterior, Interior, Trim, Interior Features,
 *   Additional Selections / Notes) WITH INLINE EDITING for Spec House
 *   projects. Additional Selections / Notes is editable on all project
 *   types, including Custom Home.
 * - Paint Tab: Paint color selections
 * - Purchases Tab: Owner purchases
 * - Documents Tab: Selection documentation by category
 *
 * Uses conditional logic to show/hide fields and mark missing selections.
 * Spec House projects get full inline editing capabilities using the same
 * hooks/mutations as the client portal (useUpdateClientSelections,
 * useSelectionFieldOptions).
 *
 * Location: src/pages/admin/components/HomeSelections.js
 *
 * @version 3.1.0
 * @updated 2026-07-21 - Additional Selections / Notes is now admin-editable
 *   on all project types via the alwaysEditable section flag.
 */

import React, { useState, useRef, useEffect } from "react";
import {
  FaClipboardList,
  FaFillDrip,
  FaShoppingCart,
  FaFolderOpen,
  FaExclamationCircle,
  FaLayerGroup,
  FaFilePdf,
  FaFileUpload,
  FaSwatchbook,
  FaWarehouse,
  FaDoorOpen,
  FaStream,
  FaBath,
  FaTimes,
  FaExternalLinkAlt,
  FaPrint,
  FaFileDownload,
  FaPencilAlt,
  FaSave,
  FaCheck,
  FaEye,
  FaPlus,
  FaTrash,
  FaStickyNote,
  FaEllipsisH,
} from "react-icons/fa";
import SelectionDocsUploadModal from "../../../../components/admin/SelectionDocsUploadModal";
import SingleCategoryUploadModal from "../../../../components/admin/SingleCategoryUploadModal";
import AddPaintModal from "../../../../components/AddPaintModal";
import AddOPMModal from "../../../../components/AddOPMModal";
import DocumentViewerModal from "../../../../components/admin/DocumentViewerModal";
import InfoModal from "../../../../components/InfoModal";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { formatDateET } from "../../../../utils/dateUtils";
import {
  handlePrintSelections,
  handleDownloadCSV,
} from "../../../../utils/selectionsExport";
import SelectionsPrintLayout from "../../../../components/SelectionsPrintLayout";
import "../../../../styles/SelectionsPrint.css";
import {
  useUpdateClientSelections,
  useSelectionFieldOptions,
  useMarkSelectionRemoved,
  useDeleteSelectionDocument,
} from "../../../../hooks/useSelections";
import "./HomeSelections.css";

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Selection tabs configuration
 * @constant {Array<Object>}
 */
const SELECTION_TABS = [
  { id: "overview", label: "Overview", icon: FaClipboardList },
  { id: "paint", label: "Paint Selections", icon: FaFillDrip },
  { id: "purchases", label: "Owner Purchases", icon: FaShoppingCart },
  { id: "documents", label: "Selection Documents", icon: FaFolderOpen },
];

/**
 * Document categories for Documents tab
 * @constant {Array<Object>}
 */
const DOCUMENT_CATEGORIES = [
  { key: "roofGutter", label: "Gutters" },
  { key: "doorsWindows", label: "Doors & Windows" },
  { key: "garageDoor", label: "Garage Door" },
  { key: "fireplace", label: "Fireplace" },
  { key: "cabinets", label: "Cabinets" },
  { key: "countertopsSinks", label: "Countertops & Sinks" },
  { key: "showersTubs", label: "Shower Glass" },
  { key: "plumbingFixtures", label: "Plumbing Fixtures" },
  { key: "lightingFixtures", label: "Lighting Fixtures" },
  { key: "flooring", label: "Flooring" },
  { key: "tile", label: "Tile" },
  { key: "finalSelectionsPDF", label: "Final Selections PDF" },
];

/**
 * Overview section configuration mapping IDs to components and metadata
 * @constant {Array<Object>}
 */
const OVERVIEW_SECTIONS = [
  {
    id: "exterior",
    label: "Exterior",
    icon: FaWarehouse,
    Component: null, // Set below after component definitions
  },
  {
    id: "interior",
    label: "Interior",
    icon: FaDoorOpen,
    Component: null,
  },
  { id: "trim", label: "Interior Trim", icon: FaStream, Component: null },
  {
    id: "interiorFeatures",
    label: "Interior Features",
    icon: FaBath,
    Component: null,
  },
  {
    id: "additionalNotes",
    label: "Additional Selections / Notes",
    icon: FaStickyNote,
    // Admin-editable on every project type, not just Spec House
    alwaysEditable: true,
    Component: null,
  },
];

// ============================================================================
// SHARED SUB-COMPONENTS
// ============================================================================

/**
 * Section wrapper component
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {JSX.Element} props.icon - Section icon
 * @param {JSX.Element} [props.children] - Section content
 * @param {JSX.Element} [props.action] - Action button(s) in header
 */
const Section = ({ title, icon, children, action }) => (
  <div className="apd-section">
    <div className="apd-section-header">
      <div className="apd-section-title">
        {icon && <span className="apd-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {action && <div className="apd-section-action">{action}</div>}
    </div>
    <div className="apd-section-content">{children}</div>
  </div>
);

/**
 * Tab navigation component
 * @param {Object} props
 * @param {Array} props.tabs - Tab configuration array
 * @param {string} props.activeTab - Currently active tab ID
 * @param {Function} props.onTabChange - Tab change handler
 */
const TabNav = ({ tabs, activeTab, onTabChange }) => (
  <div className="apd-selection-tabs">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        className={`apd-selection-tab 
          ${activeTab === tab.id ? "active" : ""}`}
        onClick={() => onTabChange(tab.id)}
      >
        <tab.icon />
        <span className="hide-sm">{tab.label}</span>
      </button>
    ))}
  </div>
);

/**
 * Small attribution badge shown on a field/row last touched by a team
 * member. `adminEdit` is the attribution entry, or null/undefined when
 * the value was not last set by an admin.
 *
 * Paint / owner-purchase rows carry an `action` ("added" or "updated")
 * so the badge can name what happened; overview fields have no action
 * and fall back to "Set by admin". `verbOverride` forces a specific
 * verb regardless of `action` (e.g. "Updated" for the free-text notes
 * field, which is only ever edited, never freshly "set").
 *
 * @param {Object} props
 * @param {{name: string, at: string, action?: string}|null}
 *   [props.adminEdit] - Attribution entry
 * @param {string} [props.verbOverride] - Force the badge verb
 */
const AdminBadge = ({ adminEdit, verbOverride }) => {
  if (!adminEdit) return null;

  const VERBS = { added: "Added", updated: "Updated" };
  const verb = verbOverride || VERBS[adminEdit.action] || "Set";

  const who = adminEdit.name || "an admin";
  const when = adminEdit.at ? ` on ${formatDateET(adminEdit.at)}` : "";

  return (
    <span className="hs-admin-badge" data-tooltip={`${verb} by ${who}${when}`}>
      {verb} by admin
    </span>
  );
};

/**
 * Row overflow ("kebab") menu for paint / owner-purchase cards.
 * Renders a horizontal-ellipsis trigger that opens a small menu of
 * actions. Follows the same backdrop + absolute menu pattern as the
 * Print/Download dropdown in this file.
 *
 * @param {Object} props
 * @param {Function} props.onEdit - Handler for the Edit action
 * @param {Function} [props.onDelete] - Handler for the Remove action;
 *   omit to render an edit-only menu
 * @param {string} [props.editLabel="Edit"] - Edit action label
 * @param {string} [props.deleteLabel="Remove"] - Remove action label
 */
const RowActionsMenu = ({
  onEdit,
  onDelete,
  editLabel = "Edit",
  deleteLabel = "Remove",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="hs-row-actions">
      <button
        type="button"
        className="hs-row-actions-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-tooltip="Actions"
      >
        <FaEllipsisH />
      </button>
      {isOpen && (
        <>
          <div
            className="hs-export-backdrop"
            onClick={() => setIsOpen(false)}
          />
          <div className="hs-row-actions-menu" role="menu">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                onEdit();
              }}
            >
              <FaPencilAlt /> {editLabel}
            </button>
            {onDelete && (
              <button
                type="button"
                role="menuitem"
                className="hs-row-actions-danger"
                onClick={() => {
                  setIsOpen(false);
                  onDelete();
                }}
              >
                <FaTrash /> {deleteLabel}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Selection field display component (read-only)
 * Shows field value or "(!) Selection Needed" if empty
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {*} props.value - Field value
 * @param {boolean} [props.isBoolean=false] - Whether field is a boolean
 * @param {Object} [props.adminEdit] - Admin attribution entry, if any
 */
const SelectionField = ({
  label,
  value,
  isBoolean = false,
  overrideDisplay = undefined,
  adminEdit = null,
}) => {
  let displayValue;
  let needsSelection = false;

  if (overrideDisplay !== undefined) {
    // Force a normal (complete) state with custom text — selection made elsewhere
    displayValue = overrideDisplay;
  } else if (isBoolean) {
    if (value === true) {
      displayValue = "Yes";
    } else {
      displayValue = (
        <>
          <FaExclamationCircle /> Selection Needed
        </>
      );
      needsSelection = true;
    }
  } else {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      displayValue = (
        <>
          <FaExclamationCircle /> Selection Needed
        </>
      );
      needsSelection = true;
    } else {
      displayValue = value;
    }
  }

  return (
    <div className="hs-selection-field">
      <span className="hs-field-label">
        {label}
        <AdminBadge adminEdit={adminEdit} />
      </span>
      <span
        className={`hs-field-value ${needsSelection ? "needs-selection" : ""}`}
      >
        {displayValue}
      </span>
    </div>
  );
};

/**
 * Vendor ordering field component (read-only)
 * Shows "Local Vendor", "Online", "No Fireplace", or "(!) Selection Needed"
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Ordering method value
 * @param {Object} [props.adminEdit] - Admin attribution entry, if any
 */
const VendorField = ({ label, value, adminEdit = null }) => {
  let displayValue;
  let needsSelection = false;

  if (!value || value.trim() === "") {
    displayValue = (
      <>
        <FaExclamationCircle /> Selection Needed
      </>
    );
    needsSelection = true;
  } else if (value === "No Fireplace" && label.includes("Fireplace")) {
    displayValue = "No Fireplace";
  } else {
    displayValue = value;
  }

  return (
    <div className="hs-selection-field">
      <span className="hs-field-label">
        {label}
        <AdminBadge adminEdit={adminEdit} />
      </span>
      <span
        className={`hs-field-value ${needsSelection ? "needs-selection" : ""}`}
      >
        {displayValue}
      </span>
    </div>
  );
};

// ============================================================================
// EDIT MODE FIELD COMPONENTS (Spec House only)
// ============================================================================

/**
 * Text input field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.infoAction] - Optional click handler for eye icon
 */
const EditTextField = ({ label, value, onChange, infoAction }) => (
  <div className="hs-edit-field">
    <span className="hs-edit-field-label">
      {label}
      {infoAction && (
        <span data-tooltip="View options">
          <FaEye className="hs-field-info-eye" onClick={infoAction} />
        </span>
      )}
    </span>
    <input
      type="text"
      className="hs-edit-input"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/**
 * Auto-expanding long-text field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Change handler
 * @param {number} [props.maxLength=5000] - Character limit
 */
const EditTextAreaField = ({ label, value, onChange, maxLength = 5000 }) => {
  const textareaRef = useRef(null);

  const resize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    resize(textareaRef.current);
  }, [value]);

  return (
    <div className="hs-edit-field hs-edit-field--full">
      <span className="hs-edit-field-label">{label}</span>
      <textarea
        ref={textareaRef}
        className="hs-edit-textarea"
        value={value || ""}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onInput={(e) => resize(e.target)}
      />
      <span className="hs-edit-char-counter">
        {(value || "").length}/{maxLength}
      </span>
    </div>
  );
};

/**
 * Select dropdown field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Array<string>} props.options - Dropdown options
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.infoAction] - Optional click handler for eye icon
 */
const EditSelectField = ({
  label,
  value,
  options,
  onChange,
  infoAction,
  disabled = false,
}) => (
  <div className="hs-edit-field">
    <span className="hs-edit-field-label">
      {label}
      {infoAction && (
        <span data-tooltip="View options">
          <FaEye className="hs-field-info-eye" onClick={infoAction} />
        </span>
      )}
    </span>
    <select
      className="hs-edit-select"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">-- Select --</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

/**
 * Checkbox toggle field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {boolean} props.checked - Current checked state
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.infoAction] - Optional click handler for eye icon
 */
const EditCheckboxField = ({ label, checked, onChange, infoAction }) => (
  <div className="hs-edit-field">
    <div className="hs-edit-checkbox-row">
      <input
        type="checkbox"
        id={`hs-edit-${label.replace(/\s/g, "-")}`}
        checked={checked || false}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label
        className="hs-edit-checkbox-label"
        htmlFor={`hs-edit-${label.replace(/\s/g, "-")}`}
      >
        {label}
        {infoAction && (
          <span data-tooltip="View options">
            <FaEye
              className="hs-field-info-eye"
              onClick={(e) => {
                e.preventDefault();
                infoAction();
              }}
            />{" "}
          </span>
        )}
      </label>
    </div>
  </div>
);

/**
 * Multi-select checkboxes for edit mode (e.g. Siding Types)
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {Array<string>} props.selected - Currently selected values
 * @param {Array<string>} props.options - Available options
 * @param {Function} props.onChange - Change handler (receives full array)
 */
const EditMultiSelectField = ({ label, selected, options, onChange }) => {
  const selectedArray = Array.isArray(selected) ? selected : [];

  /**
   * Toggle an option on/off in the selected array
   * @param {string} option - The option value to toggle
   */
  const handleToggle = (option) => {
    if (selectedArray.includes(option)) {
      onChange(selectedArray.filter((s) => s !== option));
    } else {
      onChange([...selectedArray, option]);
    }
  };

  return (
    <div className="hs-edit-field">
      <span className="hs-edit-field-label">{label}</span>
      <div className="hs-edit-multi-select">
        {options.map((opt) => (
          <div key={opt} className="hs-edit-multi-option">
            <input
              type="checkbox"
              id={`hs-multi-${opt.replace(/\s/g, "-")}`}
              checked={selectedArray.includes(opt)}
              onChange={() => handleToggle(opt)}
            />
            <label htmlFor={`hs-multi-${opt.replace(/\s/g, "-")}`}>{opt}</label>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Save/Cancel action bar for edit mode sections
 * @param {Object} props
 * @param {Function} props.onSave - Save handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isSaving - Whether save is in progress
 */
const EditActions = ({ onSave, onCancel, isSaving }) => (
  <div className="hs-edit-actions">
    <button
      className="hs-edit-cancel-btn"
      onClick={onCancel}
      disabled={isSaving}
    >
      <FaTimes /> Cancel
    </button>
    <button className="hs-edit-save-btn" onClick={onSave} disabled={isSaving}>
      {isSaving ? (
        <>Saving...</>
      ) : (
        <>
          <FaSave /> Save Changes
        </>
      )}
    </button>
  </div>
);

// ============================================================================
// OVERVIEW TAB - 4 SECTIONS WITH CONDITIONAL INLINE EDITING
// ============================================================================

/**
 * Exterior selections subsection with optional inline editing
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler for eye icon actions
 */
const ExteriorSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;
  const sidingTypesArray = Array.isArray(data.sidingTypes)
    ? data.sidingTypes
    : [];
  const isMetalRoof = data.roofMaterial === "Metal";

  if (isEditing) {
    return (
      <div className="hs-fields-grid">
        <EditSelectField
          label="Roof Material"
          value={data.roofMaterial}
          options={fieldOptions?.roofMaterial || []}
          onChange={(val) => {
            onFieldChange("roofMaterial", val);
            // Metal roof color is chosen via the metal supplier, not this dropdown
            if (val === "Metal") onFieldChange("roofColor", "");
          }}
        />
        <EditSelectField
          label="Roof Material Color"
          value={data.roofColor}
          options={fieldOptions?.roofColor || []}
          onChange={(val) => onFieldChange("roofColor", val)}
          disabled={isMetalRoof}
          infoAction={() => onViewOptions("roofColor")}
        />
        <EditSelectField
          label="Gutter/Downspout Color"
          value={data.gutterColor}
          options={fieldOptions?.gutterColor || []}
          onChange={(val) => onFieldChange("gutterColor", val)}
          infoAction={() => onViewOptions("gutterColor")}
        />
        <EditSelectField
          label="Garage Door Chosen"
          value={data.garageDoorsChosen}
          options={fieldOptions?.garageDoorsChosen || []}
          onChange={(val) => onFieldChange("garageDoorsChosen", val)}
        />
        <EditMultiSelectField
          label="Siding Type(s)"
          selected={data.sidingTypes}
          options={fieldOptions?.sidingTypes || []}
          onChange={(val) => onFieldChange("sidingTypes", val)}
        />
        {sidingTypesArray.includes("Fiber Cement Board") && (
          <EditSelectField
            label="Fiber Cement Board Option"
            value={data.fiberCementBoard}
            options={fieldOptions?.fiberCementBoard || []}
            onChange={(val) => onFieldChange("fiberCementBoard", val)}
            infoAction={() => onViewOptions("fiberCementBoard")}
          />
        )}
        {sidingTypesArray.includes("Brick") && (
          <EditCheckboxField
            label="Brick Chosen"
            checked={data.brickChosen}
            onChange={(val) => onFieldChange("brickChosen", val)}
          />
        )}
        {sidingTypesArray.includes("Stone") && (
          <EditCheckboxField
            label="Stone Chosen"
            checked={data.stoneChosen}
            onChange={(val) => onFieldChange("stoneChosen", val)}
          />
        )}
        {sidingTypesArray.includes("Wood") && (
          <EditCheckboxField
            label="Wood Chosen"
            checked={data.woodChosen}
            onChange={(val) => onFieldChange("woodChosen", val)}
          />
        )}
        <EditCheckboxField
          label="Exterior Doors & Windows Chosen"
          checked={data.exteriorDoorsWindowsChosen}
          onChange={(val) => onFieldChange("exteriorDoorsWindowsChosen", val)}
        />
      </div>
    );
  }

  // Read-only mode
  const sidingDisplay =
    sidingTypesArray.length > 0 ? sidingTypesArray.join(", ") : "";

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="hs-fields-grid">
      <SelectionField
        label="Roof Material"
        value={selections.roofMaterial}
        adminEdit={adminEdits.roofMaterial}
      />
      <SelectionField
        label="Roof Material Color"
        value={selections.roofColor}
        adminEdit={adminEdits.roofColor}
        overrideDisplay={
          selections.roofMaterial === "Metal"
            ? "Selected via Metal Supplier"
            : undefined
        }
      />
      <SelectionField
        label="Gutter/Downspout Color"
        value={selections.gutterColor}
        adminEdit={adminEdits.gutterColor}
      />
      <SelectionField
        label="Garage Door Chosen"
        value={selections.garageDoorsChosen}
        adminEdit={adminEdits.garageDoorsChosen}
      />
      <SelectionField
        label="Siding Type"
        value={sidingDisplay}
        adminEdit={adminEdits.sidingTypes}
      />
      {sidingTypesArray.includes("Fiber Cement Board") && (
        <SelectionField
          label="Fiber Cement Board Option"
          value={selections.fiberCementBoard}
          adminEdit={adminEdits.fiberCementBoard}
        />
      )}
      {sidingTypesArray.includes("Brick") && (
        <SelectionField
          label="Brick Chosen"
          value={selections.brickChosen}
          isBoolean={true}
          adminEdit={adminEdits.brickChosen}
        />
      )}
      {sidingTypesArray.includes("Stone") && (
        <SelectionField
          label="Stone Chosen"
          value={selections.stoneChosen}
          isBoolean={true}
          adminEdit={adminEdits.stoneChosen}
        />
      )}
      {sidingTypesArray.includes("Wood") && (
        <SelectionField
          label="Wood Chosen"
          value={selections.woodChosen}
          isBoolean={true}
          adminEdit={adminEdits.woodChosen}
        />
      )}
      <SelectionField
        label="Exterior Doors & Windows Chosen"
        value={selections.exteriorDoorsWindowsChosen}
        isBoolean={true}
        adminEdit={adminEdits.exteriorDoorsWindowsChosen}
      />
    </div>
  );
};

/**
 * Interior selections subsection with optional inline editing
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler for eye icon actions
 */
const InteriorSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;

  if (isEditing) {
    return (
      <div className="hs-fields-grid">
        <EditSelectField
          label="Door Pattern"
          value={data.interiorDoorPattern}
          options={fieldOptions?.interiorDoorPattern || []}
          onChange={(val) => onFieldChange("interiorDoorPattern", val)}
          infoAction={() => onViewOptions("interiorDoorPattern")}
        />
        <EditSelectField
          label="Core Type"
          value={data.interiorDoorCore}
          options={fieldOptions?.interiorDoorCore || []}
          onChange={(val) => onFieldChange("interiorDoorCore", val)}
        />
        <EditTextField
          label="Hinge Finish"
          value={data.hingeFinish}
          onChange={(val) => onFieldChange("hingeFinish", val)}
        />
        <EditCheckboxField
          label="Door Knobs Purchased"
          checked={data.doorKnobsPurchased}
          onChange={(val) => onFieldChange("doorKnobsPurchased", val)}
        />
        <EditSelectField
          label="Flooring Direction"
          value={data.flooringDirection}
          options={fieldOptions?.flooringDirection || []}
          onChange={(val) => onFieldChange("flooringDirection", val)}
          infoAction={() => onViewOptions("flooringDirection")}
        />
        <EditSelectField
          label="Painted Closet Shelves"
          value={data.closetShelving}
          options={fieldOptions?.closetShelving || []}
          onChange={(val) => onFieldChange("closetShelving", val)}
          infoAction={() => onViewOptions("closetShelving")}
        />
        <EditSelectField
          label="Switch Plate Color"
          value={data.switchPlateColor}
          options={fieldOptions?.switchPlateColor || []}
          onChange={(val) => onFieldChange("switchPlateColor", val)}
        />
        <EditTextField
          label="Vent Style/Color"
          value={data.ventStyle}
          onChange={(val) => onFieldChange("ventStyle", val)}
        />
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="hs-fields-grid">
      <SelectionField
        label="Door Pattern"
        value={selections.interiorDoorPattern}
        adminEdit={adminEdits.interiorDoorPattern}
      />
      <SelectionField
        label="Core Type"
        value={selections.interiorDoorCore}
        adminEdit={adminEdits.interiorDoorCore}
      />
      <SelectionField
        label="Hinge Finish"
        value={selections.hingeFinish}
        adminEdit={adminEdits.hingeFinish}
      />
      <SelectionField
        label="Door Knobs Purchased"
        value={selections.doorKnobsPurchased}
        isBoolean={true}
        adminEdit={adminEdits.doorKnobsPurchased}
      />
      <SelectionField
        label="Flooring Direction"
        value={selections.flooringDirection}
        adminEdit={adminEdits.flooringDirection}
      />
      <SelectionField
        label="Painted Closet Shelves"
        value={selections.closetShelving}
        adminEdit={adminEdits.closetShelving}
      />
      <SelectionField
        label="Switch Plate Color"
        value={selections.switchPlateColor}
        adminEdit={adminEdits.switchPlateColor}
      />
      <SelectionField
        label="Vent Style/Color"
        value={selections.ventStyle}
        adminEdit={adminEdits.ventStyle}
      />
    </div>
  );
};

/**
 * Trim selections subsection with optional inline editing
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler for eye icon actions (unused in Trim)
 */
const TrimSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;

  if (isEditing) {
    return (
      <div className="hs-fields-grid">
        <EditSelectField
          label="Door & Window Trim Style"
          value={data.casingStyle}
          options={fieldOptions?.casingStyle || []}
          onChange={(val) => onFieldChange("casingStyle", val)}
          infoAction={() => onViewOptions("casingStyle")}
        />
        <EditSelectField
          label="Trim Detail"
          value={data.trimDetail}
          options={fieldOptions?.trimDetail || []}
          onChange={(val) => onFieldChange("trimDetail", val)}
          infoAction={() => onViewOptions("trimDetail")}
        />
        <EditSelectField
          label="Baseboard Height"
          value={data.baseboardHeight}
          options={fieldOptions?.baseboardHeight || []}
          onChange={(val) => onFieldChange("baseboardHeight", val)}
        />
        <EditSelectField
          label="Crown Moulding Size"
          value={data.crownMouldingSize}
          options={fieldOptions?.crownMouldingSize || []}
          onChange={(val) => onFieldChange("crownMouldingSize", val)}
        />
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="hs-fields-grid">
      <SelectionField
        label="Door & Window Trim Style"
        value={selections.casingStyle}
        adminEdit={adminEdits.casingStyle}
      />
      <SelectionField
        label="Trim Detail"
        value={selections.trimDetail}
        adminEdit={adminEdits.trimDetail}
      />
      <SelectionField
        label="Baseboard Height"
        value={selections.baseboardHeight}
        adminEdit={adminEdits.baseboardHeight}
      />
      <SelectionField
        label="Crown Moulding Size"
        value={selections.crownMouldingSize}
        adminEdit={adminEdits.crownMouldingSize}
      />
    </div>
  );
};

/**
 * Interior Features subsection with optional inline editing
 * Uses smart fallback: renders dropdown if options exist, text input if not
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler for eye icon actions
 */
const InteriorFeaturesSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;

  /**
   * Render a select if options exist for this field, otherwise text input
   * @param {string} label - Display label
   * @param {string} fieldKey - Frontend field key
   */
  const renderVendorEditField = (label, fieldKey) => {
    const options = fieldOptions?.[fieldKey];

    if (options && options.length > 0) {
      return (
        <EditSelectField
          label={label}
          value={data[fieldKey]}
          options={options}
          onChange={(val) => onFieldChange(fieldKey, val)}
        />
      );
    }
    return (
      <EditTextField
        label={label}
        value={data[fieldKey]}
        onChange={(val) => onFieldChange(fieldKey, val)}
      />
    );
  };

  if (isEditing) {
    return (
      <div className="hs-fields-grid">
        {renderVendorEditField("Cabinets Chosen", "cabinetsOrdered")}
        {renderVendorEditField("Counters/Sinks Chosen", "countersSinksOrdered")}
        {renderVendorEditField("Showers/Tubs Chosen", "showersTubsOrdered")}
        {renderVendorEditField("Shower Doors Chosen", "showerDoorsOrdered")}
        {renderVendorEditField("Fireplace Chosen", "fireplaceOrdered")}
        {renderVendorEditField("Floors Chosen", "floorsOrdered")}
        {renderVendorEditField("Tile Chosen", "tileOrdered")}
        {renderVendorEditField(
          "Plumbing Fixtures Chosen",
          "plumbingFixturesOrdered",
        )}
        {renderVendorEditField(
          "Lighting Fixtures Chosen",
          "lightingFixturesOrdered",
        )}
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="hs-fields-grid">
      <VendorField
        label="Cabinets Chosen"
        value={selections.cabinetsOrdered}
        adminEdit={adminEdits.cabinetsOrdered}
      />
      <VendorField
        label="Counters/Sinks Chosen"
        value={selections.countersSinksOrdered}
        adminEdit={adminEdits.countersSinksOrdered}
      />
      <VendorField
        label="Showers/Tubs Chosen"
        value={selections.showersTubsOrdered}
        adminEdit={adminEdits.showersTubsOrdered}
      />
      <VendorField
        label="Shower Doors Chosen"
        value={selections.showerDoorsOrdered}
        adminEdit={adminEdits.showerDoorsOrdered}
      />
      <VendorField
        label="Fireplace Chosen"
        value={selections.fireplaceOrdered}
        adminEdit={adminEdits.fireplaceOrdered}
      />
      <VendorField
        label="Floors Chosen"
        value={selections.floorsOrdered}
        adminEdit={adminEdits.floorsOrdered}
      />
      <VendorField
        label="Tile Chosen"
        value={selections.tileOrdered}
        adminEdit={adminEdits.tileOrdered}
      />
      <VendorField
        label="Plumbing Fixtures Chosen"
        value={selections.plumbingFixturesOrdered}
        adminEdit={adminEdits.plumbingFixturesOrdered}
      />
      <VendorField
        label="Lighting Fixtures Chosen"
        value={selections.lightingFixturesOrdered}
        adminEdit={adminEdits.lightingFixturesOrdered}
      />
    </div>
  );
};

/**
 * Additional selections / notes subsection with inline editing
 * Freeform long-text for selections not covered by the structured fields
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 */
const AdditionalNotesSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
}) => {
  if (isEditing) {
    return (
      <div className="hs-fields-grid">
        <EditTextAreaField
          label="Selections Not Covered Above or Notes On Above Selections"
          value={editData.additionalNotes}
          onChange={(val) => onFieldChange("additionalNotes", val)}
        />
      </div>
    );
  }

  const notes = (selections.additionalNotes || "").trim();
  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="hs-selection-field hs-notes-readonly">
      <span className="hs-field-label">
        Selections Not Covered Above or Notes On Above Selections
        <AdminBadge
          adminEdit={adminEdits.additionalNotes}
          verbOverride="Updated"
        />
      </span>
      {notes ? (
        <p className="hs-notes-value">{selections.additionalNotes}</p>
      ) : (
        <p className="hs-notes-empty">No notes yet</p>
      )}
    </div>
  );
};

// Wire up OVERVIEW_SECTIONS with actual components
OVERVIEW_SECTIONS[0].Component = ExteriorSection;
OVERVIEW_SECTIONS[1].Component = InteriorSection;
OVERVIEW_SECTIONS[2].Component = TrimSection;
OVERVIEW_SECTIONS[3].Component = InteriorFeaturesSection;
OVERVIEW_SECTIONS[4].Component = AdditionalNotesSection;

// ============================================================================
// OVERVIEW TAB WITH CONDITIONAL EDITING (Spec House)
// ============================================================================

/**
 * Overview tab content with 5 subsections and optional inline editing
 * Manages per-section edit state, local edit data, and save/cancel
 * Editing is available when isSpecHouse is true, or for any section
 * flagged alwaysEditable in OVERVIEW_SECTIONS (Additional Selections /
 * Notes), which admins can edit on every project type
 *
 * @param {Object} props
 * @param {Object} props.data - Client selections data object
 * @param {string} props.recordId - Airtable record ID for the selections row
 * @param {string} props.projectId - Airtable project record ID (for attribution)
 * @param {boolean} props.isSpecHouse - Whether this is a Spec House project
 * @param {Object} props.updateMutation - useUpdateClientSelections mutation
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 */
const OverviewTab = ({
  data,
  recordId,
  projectId,
  isSpecHouse,
  updateMutation,
  fieldOptions,
}) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [viewOptionsModal, setViewOptionsModal] = useState({
    isOpen: false,
    title: "",
    content: null,
  });

  // ========================================================================
  // FIELD VIEW OPTIONS CONFIG (link and modal types only — no scrollToVendor)
  // ========================================================================

  /**
   * Configuration for field "view options" eye icons.
   * Each entry defines either a direct external link or modal content
   * (supplier links, reference images) shown when the FaEye icon is clicked.
   * Admin version excludes scrollToVendor since there are no vendor cards.
   * @constant {Object.<string, Object>}
   */
  const FIELD_VIEW_OPTIONS = {
    roofColor: {
      type: "modal",
      title: "Roof Color Options",
      /** @param {Object} currentData - Current edit data for conditional logic */
      getContent: (currentData) => {
        const isShingle = currentData?.roofMaterial === "Shingle";
        const isMetal = currentData?.roofMaterial === "Metal";
        const links = [];
        if (isShingle || (!isShingle && !isMetal)) {
          links.push({
            label: "View Shingle Colors (CertainTeed)",
            url: "https://www.certainteed.com/products/residential-roofing-products/landmark",
          });
        }
        if (isMetal || (!isShingle && !isMetal)) {
          links.push({
            label: "View Metal Options (B&M Metals)",
            url: "https://bmmetalroofing.com/",
          });
        }
        return { links };
      },
    },
    gutterColor: {
      type: "link",
      url: "https://senox.com/colors/",
    },
    fiberCementBoard: {
      type: "modal",
      title: "Fiber Cement Board Options",
      getContent: () => ({
        images: [
          { src: "/hardie-plank-smooth.webp", label: "Smooth" },
          { src: "/hardie-plank-textured.webp", label: "Textured" },
        ],
      }),
    },
    interiorDoorPattern: {
      type: "modal",
      title: "Door Patterns",
      getContent: () => ({
        images: [
          {
            src: "/New Door Patterns_2026.png",
            label: "Door Pattern Options",
          },
        ],
      }),
    },
    flooringDirection: {
      type: "modal",
      title: "Flooring Direction",
      getContent: () => ({
        images: [
          {
            src: "/parallel-floors.jpg",
            label: "Parallel to Main Door",
          },
          {
            src: "/perpendicular-floors.jpg",
            label: "Perpendicular to Main Door",
          },
        ],
      }),
    },
    closetShelving: {
      type: "modal",
      title: "Closet Shelves Options",
      getContent: () => ({
        images: [
          { src: "/closet.png", label: "Unpainted Shelves" },
          { src: "/painted shelves.png", label: "Painted Shelves" },
        ],
      }),
    },
    casingStyle: {
      type: "modal",
      title: "Casing Style Options",
      getContent: () => ({
        images: [{ src: "/trim_styles.png", label: "Casing Style Options" }],
      }),
    },
    trimDetail: {
      type: "modal",
      title: "Trim Detail Options",
      getContent: () => ({
        images: [{ src: "/moulding.png", label: "Trim Detail Options" }],
      }),
    },
  };

  /**
   * Handle eye icon click for a field's view options.
   * Opens an external link or shows a modal with images/links.
   * @param {string} fieldKey - Key matching FIELD_VIEW_OPTIONS config
   */
  const handleViewOptions = (fieldKey) => {
    const config = FIELD_VIEW_OPTIONS[fieldKey];
    if (!config) return;

    if (config.type === "link") {
      window.open(config.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (config.type === "modal") {
      const content = config.getContent(editData);
      setViewOptionsModal({
        isOpen: true,
        title: config.title,
        content,
      });
    }
  };

  if (!data) {
    return (
      <div className="apd-empty-message">
        <FaClipboardList />
        <p>No selections data available.</p>
      </div>
    );
  }

  /**
   * Enter edit mode for a specific section
   * Copies current data into local edit state
   * @param {string} sectionId - Section identifier
   */
  const handleStartEdit = (sectionId) => {
    setEditData({ ...data });
    setEditingSection(sectionId);
    setSaveSuccess(null);
    setSaveError(null);
  };

  /**
   * Whether the current edit session has unsaved changes
   * @return {boolean}
   */
  const hasUnsavedChanges = () => {
    const skip = [
      "id",
      "projectName",
      "documents",
      "selectionsDueDate",
      "selectionRecordId",
    ];
    return Object.entries(editData).some(([key, value]) => {
      if (skip.includes(key)) return false;
      const original = data[key];
      return Array.isArray(value)
        ? JSON.stringify(value) !== JSON.stringify(original)
        : value !== original;
    });
  };

  /**
   * Cancel editing and reset local state
   * Confirms before discarding if there are unsaved changes
   */
  const handleCancelEdit = () => {
    if (
      hasUnsavedChanges() &&
      // eslint-disable-next-line no-alert
      !window.confirm("You have unsaved changes. Discard them?")
    ) {
      return;
    }
    setEditingSection(null);
    setEditData({});
    setSaveError(null);
  };

  /**
   * Update a single field in local edit state
   * @param {string} field - Field key (camelCase)
   * @param {*} value - New value
   */
  const handleFieldChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Save changes for the currently editing section
   * Only sends fields that actually changed
   */
  const handleSave = async () => {
    if (!recordId || !editingSection) return;

    // Build diff - only send changed fields
    const changedFields = {};
    for (const [key, value] of Object.entries(editData)) {
      // Skip non-editable/metadata fields
      if (
        key === "id" ||
        key === "projectName" ||
        key === "documents" ||
        key === "selectionsDueDate" ||
        key === "selectionRecordId"
      ) {
        continue;
      }

      const original = data[key];
      // Compare arrays by stringifying
      if (Array.isArray(value)) {
        if (JSON.stringify(value) !== JSON.stringify(original)) {
          changedFields[key] = value;
        }
      } else if (value !== original) {
        changedFields[key] = value;
      }
    }

    if (Object.keys(changedFields).length === 0) {
      // Nothing changed - just exit edit mode
      setEditingSection(null);
      setEditData({});
      return;
    }

    try {
      setSaveError(null);
      await updateMutation.mutateAsync({
        recordId,
        projectId,
        updates: changedFields,
      });
      setSaveSuccess(editingSection);
      setEditingSection(null);
      setEditData({});

      // Clear success toast after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError("Failed to save changes. Please try again.");
    }
  };

  return (
    <div className="hs-overview-container">
      <div className="hs-tab-header-row">
        <h3 className="hs-tab-header">Selections Overview</h3>
        {/* Spec House indicator */}
        {isSpecHouse && (
          <div className="hs-spec-house-badge hide-sm">
            Edit Spec House Selections Below
          </div>
        )}
      </div>

      {saveSuccess && (
        <div className="hs-save-toast">
          <FaCheck /> Selections saved successfully
        </div>
      )}
      {saveError && (
        <div className="hs-save-toast hs-save-error">
          <FaExclamationCircle /> {saveError}
        </div>
      )}

      {OVERVIEW_SECTIONS.map(({ id, label, icon: Icon, Component }) => {
        const isSectionEditing = editingSection === id;
        // All overview sections are admin-editable on every project
        // type. (alwaysEditable is retained on the config for the
        // client portal, which still gates by deadline.)
        const canEdit = true;

        return (
          <div key={id} className="hs-subsection">
            <div className="hs-subsection-header">
              <h4 className="hs-subsection-title">
                <Icon /> {label}
              </h4>
              {canEdit && !editingSection && (
                <button
                  className="hs-edit-toggle-btn"
                  onClick={() => handleStartEdit(id)}
                  data-tooltip="Edit selections"
                >
                  <FaPencilAlt /> Edit
                </button>
              )}
              {isSectionEditing && (
                <span className="hs-editing-indicator">Editing...</span>
              )}
            </div>

            <Component
              selections={data}
              isEditing={isSectionEditing}
              editData={editData}
              onFieldChange={handleFieldChange}
              fieldOptions={fieldOptions}
              onViewOptions={handleViewOptions}
            />

            {isSectionEditing && (
              <EditActions
                onSave={handleSave}
                onCancel={handleCancelEdit}
                isSaving={updateMutation.isPending}
              />
            )}
          </div>
        );
      })}

      {/* View Options Modal (images/links for field eye icons) */}
      {viewOptionsModal.isOpen && (
        <InfoModal
          isOpen={viewOptionsModal.isOpen}
          onClose={() =>
            setViewOptionsModal({ isOpen: false, title: "", content: null })
          }
          title={viewOptionsModal.title}
        >
          <div className="hs-view-options-content">
            {viewOptionsModal.content?.links?.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hs-view-option-link"
              >
                <FaExternalLinkAlt /> {link.label}
              </a>
            ))}
            {viewOptionsModal.content?.images?.map((img, i) => (
              <div key={i} className="hs-view-option-image">
                <p className="hs-view-option-image-label">{img.label}</p>
                <span data-tooltip="Click to view full size">
                  <img
                    src={img.src}
                    alt={img.label}
                    onClick={() =>
                      window.open(img.src, "_blank", "noopener,noreferrer")
                    }
                  />
                </span>
              </div>
            ))}
          </div>
        </InfoModal>
      )}
    </div>
  );
};

// ============================================================================
// PAINT TAB
// ============================================================================

/**
 * Paint tab content with add/edit/remove (admin, all project types)
 * @param {Object} props
 * @param {Array} props.paintSelections - Array of paint selection objects
 * @param {Function} props.onAdd - Handler to open Add Paint modal
 * @param {Function} props.onEdit - Handler to open Edit Paint modal
 * @param {Function} props.onDelete - Handler to confirm paint deletion
 */
const PaintTab = ({ paintSelections, onAdd, onEdit, onDelete }) => (
  <div className="apd-paint-tab">
    <div className="hs-tab-header-row">
      <h3 className="hs-tab-header">Paint Selections</h3>
      <button className="apd-add-btn" onClick={onAdd}>
        <FaPlus /> <span className="hide-sm">Add Paint</span>
      </button>
    </div>

    <div className="apd-selections-section">
      {paintSelections.length > 0 ? (
        <div className="apd-paint-list">
          {paintSelections.map((paint) => (
            <div key={paint.id} className="apd-paint-row">
              <div className="apd-paint-body">
                {paint.swColorSwatch && (
                  <div className="apd-paint-swatch">
                    <img src={paint.swColorSwatch} alt={paint.paintSelection} />
                  </div>
                )}
                <div className="apd-paint-details">
                  <div className="apd-paint-header">
                    <span className="apd-paint-name">
                      {paint.paintSelection || paint.paintCode}
                    </span>
                    {paint.isUpgrade && (
                      <span className="apd-paint-upgrade-badge">Upgrade</span>
                    )}
                    <RowActionsMenu
                      onEdit={() => onEdit(paint)}
                      onDelete={() => onDelete({ type: "paint", id: paint.id })}
                      editLabel="Edit Paint Selection"
                      deleteLabel="Remove Paint Selection"
                    />
                  </div>
                  <div className="apd-paint-meta">
                    <span>
                      <strong>Areas:</strong>{" "}
                      {paint.areas?.length
                        ? paint.areas.join(", ")
                        : "Not specified"}
                    </span>
                    <span>
                      <strong>Sheen:</strong> {paint.sheen || "Not specified"}
                    </span>
                    {paint.texture && (
                      <span>
                        <strong>Texture:</strong> {paint.texture}
                      </span>
                    )}
                  </div>
                  {paint.locationNotes && (
                    <p className="apd-paint-notes">{paint.locationNotes}</p>
                  )}
                  {paint.adminEdited && (
                    <div className="hs-card-footer">
                      <AdminBadge adminEdit={paint.adminEdited} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="apd-empty-message">
          <FaFillDrip />
          <p>No paint selections recorded</p>
        </div>
      )}
    </div>
  </div>
);

// ============================================================================
// OWNER PURCHASES TAB
// ============================================================================

/**
 * Owner Purchases tab content with add/edit (admin, all project types)
 * @param {Object} props
 * @param {Array} props.purchases - Array of owner purchase objects
 * @param {Function} props.onImageClick - Image thumbnail click handler
 * @param {Function} props.onAdd - Handler to open Add OPM modal
 * @param {Function} props.onEdit - Handler to open Edit OPM modal
 */
const OwnerPurchasesTab = ({ purchases, onImageClick, onAdd, onEdit }) => (
  <div className="apd-purchases-tab">
    <div className="hs-tab-header-row">
      <h3 className="hs-tab-header">Owner-Purchased Materials</h3>
      <button className="apd-add-btn" onClick={onAdd}>
        <FaPlus /> <span className="hide-sm">Add Purchase</span>
      </button>
    </div>

    {purchases.length > 0 ? (
      <div className="apd-purchases-list">
        {purchases.map((purchase) => (
          <div key={purchase.id} className="apd-purchase-row">
            <div className="apd-purchase-body">
              {purchase.jobsitePhoto && purchase.jobsitePhoto.length > 0 && (
                <div
                  className="apd-purchase-image"
                  onClick={() =>
                    onImageClick(purchase.jobsitePhoto[0], purchase.productName)
                  }
                >
                  <img
                    src={
                      purchase.jobsitePhoto[0].thumbnail ||
                      purchase.jobsitePhoto[0].url
                    }
                    alt={purchase.productName}
                  />
                </div>
              )}
              <div className="apd-purchase-details">
                <div className="apd-purchase-header">
                  <span className="apd-purchase-name">
                    {purchase.productName}
                  </span>
                  {purchase.productLink && (
                    <a
                      href={purchase.productLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="apd-purchase-link"
                    >
                      View Product <FaExternalLinkAlt />
                    </a>
                  )}
                  <RowActionsMenu
                    onEdit={() => onEdit(purchase)}
                    editLabel="Edit Purchase"
                  />
                </div>
                <div className="apd-purchase-meta">
                  <span>
                    <strong>Qty:</strong> {purchase.quantity || 1}
                  </span>
                  <span>
                    <strong>Location:</strong>{" "}
                    {purchase.roomPlacement || "Not specified"}
                  </span>
                  <span>
                    <strong>Delivery:</strong>{" "}
                    {purchase.deliveryMethod || "Not specified"}
                  </span>
                  {purchase.deliveryDate && (
                    <span>
                      <strong>Date:</strong>{" "}
                      {formatDateET(purchase.deliveryDate)}
                    </span>
                  )}
                </div>
                {purchase.notes && (
                  <p className="apd-purchase-notes">{purchase.notes}</p>
                )}
                {purchase.adminEdited && (
                  <div className="hs-card-footer">
                    <AdminBadge adminEdit={purchase.adminEdited} />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="apd-empty-message">
        <FaShoppingCart />
        <p>No owner purchases recorded</p>
      </div>
    )}
  </div>
);

// ============================================================================
// DOCUMENTS TAB
// ============================================================================

/**
 * Documents tab with per-category upload buttons
 * @param {Object} props
 * @param {Object} props.documents - Documents data keyed by category
 * @param {Array} props.categories - Category configuration array
 * @param {Function} props.onBulkUpload - Bulk upload handler
 * @param {Function} props.onCategoryUpload - Category-specific upload handler
 * @param {Function} props.onDocClick - Document click handler
 * @param {Function} props.onDocDelete - Document delete handler
 *   (receives { category, attachmentId, name })
 */
const DocumentsTab = ({
  documents,
  categories,
  onBulkUpload,
  onCategoryUpload,
  onDocClick,
  onDocDelete,
}) => (
  <div className="apd-documents-tab">
    <div className="hs-tab-header-row">
      <h3 className="hs-tab-header">Quotes & Invoices</h3>
      <button className="apd-add-btn" onClick={onBulkUpload}>
        <FaLayerGroup /> <span className="hide-sm">Bulk Upload</span>
      </button>
    </div>

    <div className="apd-documents-grid">
      {categories.map((category) => {
        const categoryDocs = documents?.[category.key] || [];
        return (
          <div key={category.key} className="apd-document-category">
            <div className="apd-document-category-header">
              <h4>{category.label}</h4>
              <button
                className="apd-category-upload-btn"
                onClick={() => onCategoryUpload(category.key)}
                data-tooltip-bottom={`Upload to ${category.label}`}
              >
                <FaFileUpload />
              </button>
            </div>
            {categoryDocs.length > 0 ? (
              <div className="apd-document-list">
                {categoryDocs.map((doc) => (
                  <div key={doc.id} className="apd-document-item">
                    <div
                      className="apd-document-item-main"
                      onClick={() => onDocClick(doc)}
                    >
                      <FaFilePdf />
                      <span>{doc.name}</span>
                    </div>
                    <button
                      className="hs-delete-btn apd-document-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDocDelete({
                          category: category.key,
                          attachmentId: doc.id,
                          name: doc.name,
                        });
                      }}
                      data-tooltip="Delete document"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="apd-no-docs">No documents</p>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Main Home Selections Component
 *
 * @param {Object} props
 * @param {string} props.projectId - Project ID
 * @param {string} props.projectName - Project name for modal titles
 * @param {string} props.projectType - Project type (e.g. "Spec House", "Custom Home")
 * @param {Object} props.selectionsData - All selections data from useClientSelectionsData hook
 * @param {boolean} props.isLoading - Loading state
 * @param {Function} props.onUploadComplete - Optional callback after document upload completes
 */
const HomeSelections = ({
  projectId,
  projectName,
  projectType,
  selectionsData,
  isLoading,
  onUploadComplete,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // Internal modal states
  const [selectionUploadModal, setSelectionUploadModal] = useState({
    isOpen: false,
    mode: null,
    category: null,
  });
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Paint / OPM modal states ({ isOpen, initialData }); initialData is
  // null for add mode, or the row being edited for edit mode.
  const [paintModal, setPaintModal] = useState({
    isOpen: false,
    initialData: null,
  });
  const [opmModal, setOpmModal] = useState({
    isOpen: false,
    initialData: null,
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Selections editing hooks
  const isSpecHouse = projectType === "Spec House";
  const updateSelectionsMutation = useUpdateClientSelections();
  const { data: fieldOptions } = useSelectionFieldOptions();
  const markSelectionRemoved = useMarkSelectionRemoved();
  const deleteSelectionDocument = useDeleteSelectionDocument();

  /**
   * Handle selection image click to open document viewer modal
   * @param {Object} imageData - Image data object with url/thumbnail/filename
   */
  const handleSelectionImageClick = (imageData, productName) => {
    if (imageData) {
      setSelectedDocument({
        name:
          productName ||
          imageData.filename ||
          imageData.name ||
          "Purchase Photo",
        url: imageData.url || imageData,
        thumbnail: imageData.thumbnail,
      });
    }
  };

  /** Open selection docs modal in bulk mode */
  const openSelectionUploadModal = () => {
    setSelectionUploadModal({
      isOpen: true,
      mode: "bulk",
      category: null,
    });
  };

  /** Close selection docs modal */
  const closeSelectionUploadModal = () => {
    setSelectionUploadModal({
      isOpen: false,
      mode: null,
      category: null,
    });
  };

  /**
   * Handler for category-specific selection doc upload
   * @param {string} categoryKey - The document category key
   */
  const handleCategoryUpload = (categoryKey) => {
    setSelectionUploadModal({
      isOpen: true,
      mode: "single",
      category: categoryKey,
    });
  };

  /**
   * Handler for selection document click
   * @param {Object} doc - Document object
   */
  const handleDocClick = (doc) => {
    setSelectedDocument(doc);
  };

  /** Close selection document viewer */
  const closeDocViewer = () => {
    setSelectedDocument(null);
  };

  /** Close selection upload modal */
  const closeUploadModal = () => {
    setSelectionUploadModal({
      isOpen: false,
      category: null,
    });
  };

  /**
   * Handle upload completion (for both bulk and category uploads)
   * @param {Object} result - Upload result data
   */
  const handleUploadComplete = (result) => {
    console.log("Selection docs uploaded:", result);
    closeUploadModal();
    if (onUploadComplete) {
      onUploadComplete(result);
    }
  };

  /**
   * Handle confirmed deletion from the shared confirm modal.
   * Branches on deleteConfirm.type:
   *   - "document": hard-delete a selection document attachment
   *   - any other type: soft-delete a paint / OPM selection
   * Rethrows on error so ConfirmationModal keeps itself open.
   */
  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    if (deleteConfirm.type === "document") {
      await deleteSelectionDocument.mutateAsync({
        projectId,
        category: deleteConfirm.category,
        attachmentId: deleteConfirm.attachmentId,
      });
    } else {
      await markSelectionRemoved.mutateAsync({
        type: deleteConfirm.type,
        id: deleteConfirm.id,
        projectId,
      });
    }
    setDeleteConfirm(null);
  };

  /**
   * Handler for clicking the delete button on a selection document.
   * Opens the shared confirm modal with document context.
   */
  const handleDocDelete = ({ category, attachmentId, name }) => {
    setDeleteConfirm({ type: "document", category, attachmentId, name });
  };

  if (isLoading) {
    return (
      <Section title="Home Selections" icon={<FaSwatchbook />}>
        <div className="apd-loading">Loading selections...</div>
      </Section>
    );
  }

  return (
    <Section
      title="Home Selections"
      icon={<FaSwatchbook />}
      action={
        <div className="hs-export-dropdown">
          <button
            className="apd-add-btn"
            onClick={() => setExportMenuOpen(!exportMenuOpen)}
            type="button"
          >
            <FaPrint /> <span className="hide-sm">Print / Download</span>
          </button>
          {exportMenuOpen && (
            <>
              <div
                className="hs-export-backdrop"
                onClick={() => setExportMenuOpen(false)}
              />
              <div className="hs-export-menu">
                <button
                  onClick={() => {
                    setExportMenuOpen(false);
                    handlePrintSelections();
                  }}
                >
                  <FaPrint /> Print to PDF / Printer
                </button>
                <button
                  onClick={() => {
                    setExportMenuOpen(false);
                    handleDownloadCSV(
                      selectionsData?.clientSelections,
                      selectionsData?.paintSelections,
                      projectName,
                    );
                  }}
                >
                  <FaFileDownload /> Download as CSV
                </button>
              </div>
            </>
          )}
        </div>
      }
    >
      {/* Tab Navigation */}
      <TabNav
        tabs={SELECTION_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="hs-tab-content">
        {activeTab === "overview" && (
          <OverviewTab
            data={selectionsData?.clientSelections}
            recordId={selectionsData?.clientSelections?.id}
            projectId={projectId}
            isSpecHouse={isSpecHouse}
            updateMutation={updateSelectionsMutation}
            fieldOptions={fieldOptions}
          />
        )}

        {activeTab === "paint" && (
          <PaintTab
            paintSelections={selectionsData?.paintSelections || []}
            onAdd={() => setPaintModal({ isOpen: true, initialData: null })}
            onEdit={(paint) =>
              setPaintModal({ isOpen: true, initialData: paint })
            }
            onDelete={setDeleteConfirm}
          />
        )}

        {activeTab === "purchases" && (
          <OwnerPurchasesTab
            purchases={selectionsData?.ownerPurchases || []}
            onImageClick={handleSelectionImageClick}
            onAdd={() => setOpmModal({ isOpen: true, initialData: null })}
            onEdit={(purchase) =>
              setOpmModal({ isOpen: true, initialData: purchase })
            }
          />
        )}

        {activeTab === "documents" && (
          <DocumentsTab
            documents={selectionsData?.clientSelections?.documents || {}}
            categories={DOCUMENT_CATEGORIES}
            onBulkUpload={openSelectionUploadModal}
            onCategoryUpload={handleCategoryUpload}
            onDocClick={handleDocClick}
            onDocDelete={handleDocDelete}
          />
        )}
      </div>

      {/* Internal Modals */}

      {/* Print Layout */}
      <SelectionsPrintLayout
        selections={selectionsData?.clientSelections}
        paintSelections={selectionsData?.paintSelections}
        projectName={projectName}
      />

      {/* Document Viewer Modal (shared - used for OPM images & selection docs) */}
      {selectedDocument && (
        <DocumentViewerModal doc={selectedDocument} onClose={closeDocViewer} />
      )}

      {/* Bulk Upload Selection Docs Modal */}
      {selectionUploadModal.isOpen && selectionUploadModal.mode === "bulk" && (
        <SelectionDocsUploadModal
          isOpen={true}
          onClose={closeSelectionUploadModal}
          projectId={projectId}
          projectName={projectName}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Single Category Upload Selection Docs Modal */}
      {selectionUploadModal.isOpen &&
        selectionUploadModal.mode === "single" && (
          <SingleCategoryUploadModal
            isOpen={true}
            onClose={closeSelectionUploadModal}
            projectId={projectId}
            projectName={projectName}
            category={selectionUploadModal.category}
            onUploadComplete={handleUploadComplete}
          />
        )}

      {/* ========== PAINT / OWNER-PURCHASE MODALS ========== */}

      {/* Add / Edit Paint Modal */}
      <AddPaintModal
        isOpen={paintModal.isOpen}
        onClose={() => setPaintModal({ isOpen: false, initialData: null })}
        projectId={projectId}
        mode={paintModal.initialData ? "edit" : "add"}
        initialData={paintModal.initialData}
      />

      {/* Add / Edit Owner Purchase Modal */}
      <AddOPMModal
        isOpen={opmModal.isOpen}
        onClose={() => setOpmModal({ isOpen: false, initialData: null })}
        projectId={projectId}
        mode={opmModal.initialData ? "edit" : "add"}
        initialData={opmModal.initialData}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={
          deleteConfirm?.type === "document"
            ? "Delete Document?"
            : "Remove Selection?"
        }
        message={
          deleteConfirm?.type === "document"
            ? `Are you sure you want to delete "${deleteConfirm?.name || "this document"}"?`
            : "Are you sure you want to remove this selection from the record?"
        }
        warning={
          deleteConfirm?.type === "document"
            ? "This will permanently remove this file from the selections record."
            : "This will hide the selection from active views."
        }
        proceed="This action cannot be undone."
        confirmText={deleteConfirm?.type === "document" ? "Delete" : "Remove"}
        isDestructive
        isLoading={
          markSelectionRemoved.isLoading || deleteSelectionDocument.isLoading
        }
      />
    </Section>
  );
};

export default HomeSelections;
